import * as React from "react";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { getRatingText } from "src/components/utility/Reviews";
import { useLocalization } from "@shared-ui/localization-context";

/**
 * A component for displaying the average rating and total rating count
 */

const AverageRating = ({ rating, language }: { rating: number; language?: string[] }) => (
  <span className="uitk-type-heading-300 all-r-padding-one">
    {[rating.toLocaleString(language, { maximumFractionDigits: 1 })]}
    /5
  </span>
);

const RatingTextAndRatingCount = ({
  language,
  localizationKey,
  reviewCount,
}: {
  reviewCount: number;
  language?: string[];
  localizationKey?: string;
}) => {
  const getL10nMessage = (key?: string) => {
    const reviewsAmount = reviewCount.toLocaleString(language, { maximumFractionDigits: 1 });
    const singularOrPlural = reviewCount > 1 ? "reviewCountPlural" : "reviewCountSingular";
    const locKey = `${key}.${singularOrPlural}`;
    const defaultLocKey = `hotels.review.${singularOrPlural}`;
    const { formatText } = useLocalization();

    return formatText(locKey, reviewsAmount) || formatText(defaultLocKey, reviewsAmount);
  };

  const ratingText = getL10nMessage(localizationKey);

  return <span className="uitk-type-300">({ratingText})</span>;
};

export const AggregateRating = ({
  context,
  rating,
  reviewCount,
  localizationKey,
  withSuperlative,
}: {
  context?: ExtendedContextStore;
  rating?: number;
  reviewCount?: number;
  localizationKey?: string;
  withSuperlative?: boolean;
}) => {
  if (!rating || !reviewCount || !context) {
    return null;
  }

  const language = context.locale ? context.locale.split(/[_-]/) : undefined;

  return (
    <p className="AggregateRating uitk-type-300 all-t-margin-zero">
      <AverageRating rating={rating} language={language} />
      {withSuperlative && <span className="all-r-padding-one">{getRatingText(rating)}</span>}
      {language && (
        <RatingTextAndRatingCount language={language} reviewCount={reviewCount} localizationKey={localizationKey} />
      )}
    </p>
  );
};
